// FLAGS: none
// Native <dialog> confirm/cancel prompt driven by an open prop.
import { useEffect, useRef } from "react";
import Button from "@openup/shared/components/Button/Button";
import { useTranslation } from "react-i18next";

interface ConfirmActionDialogProps {
  open: boolean;
  onCancelled: () => void;
  onConfirmed: () => void;
  title: string;
  description: string;
  submitting?: boolean;
  "data-cy"?: string;
}

const ConfirmActionDialog = ({
  open,
  onCancelled,
  onConfirmed,
  title,
  description,
  submitting = false,
  "data-cy": dataCy = "confirm-action-dialog",
}: ConfirmActionDialogProps) => {
  const { t } = useTranslation();
  const dialogRef = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;

    if (open) {
      dialog.showModal();
    } else {
      dialog.close();
    }
  }, [open]);

  return (
    <dialog
      ref={dialogRef}
      data-cy={dataCy}
      onClose={onCancelled}
      className="rounded-lg shadow-xl p-0 backdrop:bg-indigo-950/50 max-w-md w-full"
    >
      <div className="p-6">
        <h2 className="text-lg font-semibold text-indigo-800 mb-2">{title}</h2>
        <p className="text-sm text-slate-700">{description}</p>
      </div>
      <div className="border-t border-slate-200 flex justify-end gap-3 px-6 py-4">
        <Button variant="tertiary" onClick={onCancelled} disabled={submitting}>
          {t("Common.Cancel")}
        </Button>
        <Button
          variant="primary"
          onClick={onConfirmed}
          loading={submitting}
          data-cy="confirm-action-submit"
        >
          {t("Common.Confirm")}
        </Button>
      </div>
    </dialog>
  );
};

export default ConfirmActionDialog;
